/* eslint-disable react-hooks/exhaustive-deps */
import { GoogleMap, OVERLAY_MOUSE_TARGET, OverlayViewF, Polyline } from '@react-google-maps/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';

import { Button } from '@/components/button';
import { GoogleMapsLoader } from '@/components/google-maps-loader';
import { Icon } from '@/components/icon';
import { MarkerDot, MarkerDotProps } from '@/components/map-markers/marker-dot';
import { AIRPORT, MAP_LINE_STYLE_DASHED, SEAPORT, TRANSFER_STOP } from '@/lib/constants';
import { activeMapSegmentState, mapInstanceState, mapLocationsState } from '@/state/map';
import { polylinesState } from '@/state/polylines';
import { routeSearchDestinationState, routeSearchOriginState } from '@/state/routes';
import { userDetailsState } from '@/state/user';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { getMapMarkerIcon } from '@/utils/mapping';
import { cn } from '@/utils/styles';

import { dashedPathOptions, defaultMapOptions, solidPathOptions } from './config';
import { createPathsArr, getMarkersArr } from './utils';

const BY_APP = 'app';
const BY_USER = 'user';

const MapBase = () => {
  // Get from state
  const origin = useRecoilValue(routeSearchOriginState);
  const destination = useRecoilValue(routeSearchDestinationState);
  const activeSegment = useRecoilValue(activeMapSegmentState);
  const polylines = useRecoilValue(polylinesState);
  const [mapLocations, setMapLocations] = useRecoilState(mapLocationsState);
  const [mapInstance, setMapInstance] = useRecoilState(mapInstanceState);
  const userDetailsLoadable = useRecoilValueLoadable(userDetailsState);

  // Handle waiting for userDetails to load
  const userDetails = userDetailsLoadable.state === 'hasValue' ? userDetailsLoadable.contents : null;

  // Local state to track user interaction
  const [zoomChanged, setZoomChanged] = useState<boolean>(false);
  const zoomChangedBy = useRef(BY_APP);

  // Transform incoming data
  const pathsArray = useMemo(() => createPathsArr(mapLocations, polylines), [mapLocations]);
  const markersArray = getMarkersArr(mapLocations);

  const onLoad = useCallback((loadedMap: google.maps.Map) => {
    setMapInstance(loadedMap);

    // If markers already exist show them, else use the default location
    if (markersArray.length) {
      fitMap(true, loadedMap);
    } else {
      setMapToDefaultLocation(loadedMap);
    }

    setTimeout(() => {
      setZoomChanged(false);
    }, 100);
  }, []);

  const onUnmount = useCallback(() => {
    setMapInstance(null);
  }, []);

  const setMapToDefaultLocation = (loadedMap: google.maps.Map) => {
    // Set map defaults
    loadedMap.setCenter(userDetails?.position || defaultMapOptions.center);
    loadedMap.setZoom(defaultMapOptions.zoom);
  };

  // Fits map to location markers when they change
  const fitMap = useCallback(
    (forceFitBounds = false, localMapInstance?: google.maps.Map) => {
      const map = localMapInstance || mapInstance;

      // If we have a map and locations, then update the map
      if (map && markersArray.length) {
        const bounds = new google.maps.LatLngBounds();
        // Loops through the locations and adds them to the LatLngBounds
        markersArray.forEach((marker) => {
          if (marker && marker.location) {
            bounds.extend(marker.location);
          }
        });
        // If the zoom was not changed by the user,
        // Then update it to fit the markers
        if (!zoomChanged || forceFitBounds) {
          // We set the changedBy to APP to stop a zoom lock
          zoomChangedBy.current = BY_APP;
          map.fitBounds(bounds);
          // Then reset it back to USER after
          zoomChangedBy.current = BY_USER;
        }
      }
    },
    [markersArray],
  );

  useEffect(() => {
    // Update map when markers array updates
    fitMap();
  }, [markersArray]);

  useEffect(() => {
    // Update map when user details change
    // but only if there are no markers
    if (mapInstance && !markersArray.length) {
      setMapToDefaultLocation(mapInstance);
    }
  }, [userDetails, mapInstance]);

  useEffect(() => {
    // If search Locations change, update the Map Path
    if (origin && destination) {
      setMapLocations({
        id: 'searchQuery',
        paths: [
          {
            id: 'path',
            style: MAP_LINE_STYLE_DASHED,
            locations: [origin, destination],
          },
        ],
        markers: [origin, destination],
      });
    }
    // Update the map
    fitMap(true);
  }, [origin, destination]);

  // Was the zoom changed by the user?
  const onZoomChange = () => {
    setZoomChanged(zoomChangedBy.current === BY_USER);
  };

  const onReset = () => {
    // Force map update
    fitMap(true);
    setZoomChanged(false);
  };

  const renderMap = () => (
    <>
      <div
        className={cn('absolute -bottom-12 left-1/2 z-2 -translate-x-1/2 transition-[bottom] duration-200 ease-in', {
          'bottom-6': zoomChanged,
        })}
      >
        <Button onClick={() => onReset()} size="small" variant="primaryDark">
          Reset Zoom
        </Button>
      </div>

      <GoogleMap
        mapContainerClassName="gmap"
        options={defaultMapOptions.options}
        onZoomChanged={onZoomChange}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        {/* Child components, such as markers, info windows, etc. */}
        <>
          {markersArray.map((location, index) => {
            const isStartOrEndPoint = index === 0 || index === markersArray.length - 1;
            const isActive = location.placeId === activeSegment;

            // Determine Type and color and size
            const locationType = location.type || '';
            const isStop = location.transferType === TRANSFER_STOP;
            const isAirportOrSeaport = location.type === AIRPORT || location.type === SEAPORT;
            const locationColor = isAirportOrSeaport ? getTransportColor(locationType) : 'lightBlue.500';
            // Determine marker size
            let markerSize: MarkerDotProps['size'] = isAirportOrSeaport && !isStop ? 'lg' : 'md';
            markerSize = isStop ? 'sm' : markerSize;
            markerSize = isActive ? 'xl' : markerSize;

            return location && location.location ? (
              <OverlayViewF
                key={`${location.placeId}-${index}`}
                position={location.location}
                mapPaneName={OVERLAY_MOUSE_TARGET}
              >
                <MarkerDot
                  backgroundColor={locationColor}
                  borderColor={locationColor}
                  size={markerSize}
                  isActive={isStartOrEndPoint}
                  isSelected={isActive}
                  zIndex={isStartOrEndPoint ? markersArray.length + 1 : undefined}
                  type={locationType}
                  showTooltip={isStartOrEndPoint}
                  tooltipContent={
                    isAirportOrSeaport ? <span>{location?.identifier?.value}</span> : <span>{location.name}</span>
                  }
                  tooltipVariant={isStartOrEndPoint ? 'blue' : 'white'}
                  className="border border-solid"
                  isOffset
                >
                  {isAirportOrSeaport && !isStop && (
                    <Icon name={getMapMarkerIcon(location.type, location.transferType)} color="white" />
                  )}
                </MarkerDot>
              </OverlayViewF>
            ) : null;
          })}
          {/* solid line */}
          {pathsArray.length &&
            pathsArray.map((path) => {
              const isDashedLine = path.style === MAP_LINE_STYLE_DASHED;

              if (path.positions[0] && path.positions[1] && !isDashedLine) {
                return (
                  <Polyline
                    key={`${path.id}_hover`}
                    path={path.positions}
                    options={{
                      ...solidPathOptions,
                      strokeColor: path.color,
                      strokeOpacity: 0.2,
                      strokeWeight: 8,
                      zIndex: 1,
                    }}
                  />
                );
              }
              return null;
            })}
          {/* thin line */}
          {pathsArray.length &&
            pathsArray.map((path) => {
              const isDashedLine = path.style === MAP_LINE_STYLE_DASHED;
              const pathOptions = isDashedLine ? dashedPathOptions : solidPathOptions;
              const strokeColor = path.color;
              const strokeWeight = path.id === activeSegment ? 4 : 2;

              if (path.positions[0] && path.positions[1]) {
                return (
                  <Polyline
                    key={path.id}
                    path={path.positions}
                    options={{ ...pathOptions, strokeColor, strokeWeight }}
                  />
                );
              }
              return null;
            })}
        </>
      </GoogleMap>
    </>
  );

  return (
    <div className="chromatic-ignore relative grid h-full w-full place-items-center overflow-hidden bg-grey-200 [&_.gmap]:h-full [&_.gmap]:w-full">
      {renderMap()}
    </div>
  );
};

const Map = () => {
  return (
    <GoogleMapsLoader>
      <MapBase />
    </GoogleMapsLoader>
  );
};

export { Map };
